import React from 'react'
import {
    Box,
    Popover,
    PopoverBody,
    PopoverArrow,
    PopoverTrigger,
    PopoverContent,
} from '@chakra-ui/react'
import { Icon } from '@chakra-ui/icons'
import { FaGear, FaNoteSticky, FaPaperPlane } from 'react-icons/fa6'
import { Button, ButtonGroup } from '@chakra-ui/react'

import IconButtonWithTooltip from '~components/shared/icon-button-with-tooltip'
import SharedHelpText from '~components/shared/form/shared-help-text'
import SharedLabel from '~components/shared/form/shared-label'
import SharedSwitch from '~components/shared/form/shared-switch'
import SharedStack from '~components/shared/wrappers/shared-stack'
import ShouldUpdateChatStatus from './should-update-chat-status'
import ScheduleSendButton from './schedule-send-button'
import {
    ChatFieldsFragment,
    ClientPhoneFieldsFragment,
    MessageMethod,
} from '~graphql/generated/graphql'
import { formatPhoneReadable } from '~utils/phone'
import { getPhoneCountryFromCode } from '~utils/helpers'

interface Props {
    chat: ChatFieldsFragment
    isDisabled: boolean
    isLoading: boolean
    handleSend: () => void
    isInternal?: boolean
    shouldCloseChatOnSend?: boolean
    setShouldCloseChatOnSend?: (val: boolean) => void
    shouldOpenChatOnSend?: boolean
    setShouldOpenChatOnSend?: (val: boolean) => void
    allowCtrlEnterToSend: boolean
    setAllowCtrlEnterToSend: (val: boolean) => void
    onScheduleSend?: (sendTime: number) => void
    isScheduling?: boolean
    clientPhone: ClientPhoneFieldsFragment | undefined
    messageMethods: MessageMethod[]
}

export default function SendButton({
    chat,
    isDisabled,
    isLoading,
    handleSend,
    isInternal,
    shouldCloseChatOnSend,
    setShouldCloseChatOnSend,
    shouldOpenChatOnSend,
    setShouldOpenChatOnSend,
    allowCtrlEnterToSend,
    setAllowCtrlEnterToSend,
    onScheduleSend,
    isScheduling,
    clientPhone,
    messageMethods,
}: Props) {
    const showSendOptions = setShouldCloseChatOnSend && setShouldOpenChatOnSend
    const sendButtonLabel = getSendButtonLabel(
        chat,
        isInternal,
        messageMethods,
        clientPhone,
    )

    return (
        <ButtonGroup size="xs">
            <Button
                leftIcon={
                    <Icon as={isInternal ? FaNoteSticky : FaPaperPlane} />
                }
                h="28px"
                px={3}
                rounded="full"
                isLoading={isLoading}
                colorScheme={isInternal ? 'orange' : 'brand'}
                isDisabled={isDisabled}
                onClick={handleSend}
            >
                {sendButtonLabel}
            </Button>
            {!isInternal && onScheduleSend && (
                <ScheduleSendButton
                    isDisabled={isDisabled}
                    isLoading={isScheduling ?? false}
                    onSchedule={onScheduleSend}
                />
            )}
            <Popover placement="top-end" returnFocusOnClose={false}>
                <PopoverTrigger>
                    <IconButtonWithTooltip
                        h="28px"
                        w="28px"
                        variant="subtle"
                        rounded="full"
                        borderWidth={1}
                        borderColor="gray.200"
                        borderRightRadius="full"
                        icon={<Icon as={FaGear} />}
                        tooltipOverrides={{
                            isDisabled: isDisabled,
                        }}
                        label="Send options"
                    />
                </PopoverTrigger>
                <PopoverContent w="340px" rounded="xl">
                    <PopoverArrow />
                    <PopoverBody>
                        <SharedStack spacing={4}>
                            {!isInternal && showSendOptions && (
                                <ShouldUpdateChatStatus
                                    chat={chat}
                                    shouldCloseChatOnSend={
                                        shouldCloseChatOnSend!
                                    }
                                    setShouldCloseChatOnSend={
                                        setShouldCloseChatOnSend
                                    }
                                    shouldOpenChatOnSend={
                                        shouldOpenChatOnSend!
                                    }
                                    setShouldOpenChatOnSend={
                                        setShouldOpenChatOnSend
                                    }
                                />
                            )}
                            <AllowCtrlEnterToSend
                                allowCtrlEnterToSend={allowCtrlEnterToSend}
                                setAllowCtrlEnterToSend={
                                    setAllowCtrlEnterToSend
                                }
                            />
                        </SharedStack>
                    </PopoverBody>
                </PopoverContent>
            </Popover>
        </ButtonGroup>
    )
}

function getSendButtonLabel(
    chat: ChatFieldsFragment,
    isInternal: boolean | undefined,
    messageMethods: MessageMethod[],
    clientPhone: ClientPhoneFieldsFragment | undefined,
) {
    if (isInternal) return 'Add Note'
    const methodsLabel: string[] = []
    if (messageMethods.includes(MessageMethod.Sms)) {
        if (!!clientPhone) {
            const formattedPhone =
                formatPhoneReadable(
                    clientPhone?.phone,
                    clientPhone?.country_code
                        ? getPhoneCountryFromCode(clientPhone.country_code)
                        : chat.clinic?.address?.country,
                ) || clientPhone?.phone
            const phoneName = clientPhone?.name
            methodsLabel.push(`${phoneName ?? formattedPhone}`)
        } else methodsLabel.push('SMS')
    }

    if (messageMethods.includes(MessageMethod.Email)) {
        methodsLabel.push(chat.client?.email ?? 'Email')
    }

    return `Send${methodsLabel.length > 0 ? ` to ${methodsLabel.join(' and ')}` : ''}`
}

function AllowCtrlEnterToSend({
    allowCtrlEnterToSend,
    setAllowCtrlEnterToSend,
}: {
    allowCtrlEnterToSend: boolean
    setAllowCtrlEnterToSend: (val: boolean) => void
}) {
    return (
        <SharedStack row>
            <Box flex={1}>
                <SharedLabel>Allow Ctrl+Enter to send?</SharedLabel>
                <SharedHelpText>
                    If this is checked, Ctrl+Enter (or Cmd+Enter on Mac) will
                    send the current message. Enter still adds a new line.
                </SharedHelpText>
            </Box>
            <SharedSwitch
                size="sm"
                isChecked={allowCtrlEnterToSend}
                onChange={e => setAllowCtrlEnterToSend(e.target.checked)}
            />
        </SharedStack>
    )
}
